import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Loader2, Image, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Comment {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null;
  author_email: string | null;
  is_external: boolean;
  user_id: string | null;
  image_url: string | null;
  share_link_id: string | null;
}

interface DocumentCommentsProps {
  documentId: string;
}

export function DocumentComments({ documentId }: DocumentCommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchComments();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel(`owner-comments-${documentId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
          filter: `document_id=eq.${documentId}`
        },
        (payload) => {
          const newComment = payload.new as Comment;
          setComments(prev => [...prev, newComment]);
          setTimeout(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
          }, 100);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [documentId]);

  const fetchComments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("comments")
      .select("*")
      .eq("document_id", documentId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching comments:", error);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;

    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${imageFile.name.split('.').pop()}`;
    const filePath = `owner/${documentId}/${fileName}`;

    const { error } = await supabase.storage
      .from('comment-attachments')
      .upload(filePath, imageFile);

    if (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload de l'image");
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('comment-attachments')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async () => {
    if (!newComment.trim() && !imageFile) {
      toast.error("Ajoutez un message ou une image");
      return;
    }

    setSubmitting(true);

    let imageUrl: string | null = null;
    if (imageFile) {
      imageUrl = await uploadImage();
    }

    const { error } = await supabase.from("comments").insert({
      document_id: documentId,
      body: newComment.trim(),
      author_name: "Propriétaire du document",
      is_external: false,
      image_url: imageUrl,
    });

    if (error) {
      console.error("Error submitting comment:", error);
      toast.error("Erreur lors de l'envoi");
    } else {
      setNewComment("");
      removeImage();
    }
    setSubmitting(false);
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
  };

  const unreadCount = comments.filter(c => c.is_external).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-between border-dashed"
        >
          <span className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Discussion ({comments.length})
          </span>
          <span className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge variant="default" className="text-xs">
                {unreadCount} client{unreadCount > 1 ? 's' : ''}
              </Badge>
            )}
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <div className="border rounded-lg bg-muted/30 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center p-6 text-muted-foreground">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun commentaire</p>
              <p className="text-xs mt-1">Les commentaires de vos clients apparaîtront ici</p>
            </div>
          ) : (
            <ScrollArea className="h-[200px]" ref={scrollRef}>
              <div className="p-3 space-y-3">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={cn(
                      "flex gap-2",
                      comment.is_external ? "flex-row" : "flex-row-reverse"
                    )}
                  >
                    <Avatar className="w-7 h-7 shrink-0">
                      <AvatarFallback className={cn(
                        "text-xs",
                        comment.is_external 
                          ? "bg-secondary text-secondary-foreground" 
                          : "bg-primary text-primary-foreground"
                      )}>
                        {getInitials(comment.author_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={cn(
                      "max-w-[75%] rounded-lg px-3 py-2",
                      comment.is_external 
                        ? "bg-secondary text-secondary-foreground" 
                        : "bg-primary text-primary-foreground"
                    )}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium">{comment.author_name}</span>
                        {comment.is_external && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-current">
                            Client
                          </Badge>
                        )}
                      </div>
                      {comment.body && (
                        <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                      )}
                      {comment.image_url && (
                        <img
                          src={comment.image_url}
                          alt="Pièce jointe"
                          className="mt-2 rounded max-w-full max-h-32 object-cover cursor-pointer"
                          onClick={() => window.open(comment.image_url!, '_blank')}
                        />
                      )}
                      <p className="text-[10px] opacity-70 mt-1">
                        {formatTime(comment.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* Reply form */}
          <div className="border-t p-3 bg-background/50">
            {imagePreview && (
              <div className="relative inline-block mb-2">
                <img src={imagePreview} alt="Preview" className="h-16 rounded object-cover" />
                <button
                  onClick={removeImage}
                  className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <Textarea
                placeholder="Répondre au client..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[60px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <div className="flex flex-col gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleSubmit}
                  disabled={submitting || (!newComment.trim() && !imageFile)}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
